/**
 * Base printer interface that all printer types should implement
 */
class BasePrinter {
  constructor({
    deviceName,
    innerMacAddress,
    type = "qrapidServer",
    name,
    location = "all",
    id,
    last_updated = new Date().toISOString().split("T")[0],
    last_updated_by = "admin",
    isConnected = false,
    width,
    paperSize,
  }) {
    this.deviceName = deviceName;
    this.innerMacAddress = innerMacAddress;
    this.isConnected = isConnected;
    this.connectionError = null;
    this.type = type;
    this.name = name || deviceName; // Default to deviceName if name not provided
    this.location = location;
    this.id = id;
    this.last_updated = last_updated;
    this.last_updated_by = last_updated_by;
    this.width = width;
    this.paperSize = paperSize;
  }

  /**
   * Connect to the printer
   * @returns {Promise<boolean>} True if connection successful
   */
  async connect() {
    throw new Error("connect() method must be implemented by printer type");
  }

  /**
   * Print content to the printer
   * @param {string} content - Content to print
   * @returns {Promise<boolean>} True if print successful
   */
  async print(content) {
    throw new Error("print() method must be implemented by printer type");
  }

  /**
   * Set the connection status and error
   * @param {boolean} isConnected
   * @param {string|null} error
   */
  setConnectionStatus(isConnected, error = null) {
    this.isConnected = isConnected;
    this.connectionError = error;
  }

  /**
   * Update printer details
   * @param {Object} details - New printer details
   */
  updateDetails(details) {
    Object.assign(this, {
      ...details,
      last_updated: new Date().toISOString().split("T")[0],
    });
  }

  /**
   * Create a new printer instance
   * @param {string} type - Type of printer to create
   * @param {Object} config - Printer configuration
   * @returns {BasePrinter} New printer instance
   */
  static createPrinter(type, config) {
    // This will be implemented by the PrinterFactory
    throw new Error("Use PrinterFactory to create printer instances");
  }

  /**
   * Convert printer to JSON
   * @returns {Object} JSON representation of printer
   */
  toJSON() {
    return {
      deviceName: this.deviceName,
      innerMacAddress: this.innerMacAddress,
      isConnected: this.isConnected,
      connectionError: this.connectionError,
      type: this.type,
      name: this.name,
      location: this.location,
      id: this.id,
      last_updated: this.last_updated,
      last_updated_by: this.last_updated_by,
      width: this.width,
      paperSize: this.paperSize,
    };
  }
}

export default BasePrinter;
